import {
  ESCALA_MAXIMA_POSSIVEL,
  ESCALA_MINIMA_POSSIVEL,
  MAXIMO_DE_CRITERIOS,
} from '@locaveri/shared'
import { GripVertical, Plus, Trash2, Undo2 } from 'lucide-react'
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  useConfiguracaoDaPesquisa,
  useSalvarConfiguracao,
  type ConfiguracaoDaApi,
  type CriterioParaSalvar,
} from '@/features/pesquisa/api'
import { MotivosDeCancelamento } from '@/features/pesquisa/motivos-de-cancelamento'
import { errorMessage } from '@/lib/api'
import { usePermissao } from '@/lib/permissoes'

/**
 * CONFIGURAÇÃO DA PESQUISA — critérios, escala e a alavanca comercial.
 *
 * ===========================================================================
 * A TELA TEM DUAS METADES, E ELAS NÃO SÃO DO MESMO TIPO
 * ===========================================================================
 * A primeira é MEDIÇÃO: o que se pergunta e em que escala. A segunda é
 * COMERCIAL: a partir de que nota a pessoa é levada a avaliar a empresa
 * publicamente. A referência do concorrente as mistura numa página só de campos,
 * e o resultado é que o limiar de review — a decisão de negócio da tela — fica
 * indistinguível do texto do rodapé do e-mail.
 *
 * ===========================================================================
 * ⚠️ O AVISO DO LIMIAR NÃO É DECORAÇÃO
 * ===========================================================================
 * Mandar TODO respondente para a avaliação pública parece maximizar volume, e é
 * como se coleciona uma página de avaliações ruins com o link que a própria
 * empresa mandou. A tela mostra, ao lado do campo, quantas das respostas já
 * recebidas passariam pelo corte escolhido — o número que transforma a escolha
 * de "quanto?" numa escolha informada.
 *
 * ===========================================================================
 * RENOMEAR CRITÉRIO NÃO REESCREVE O PASSADO
 * ===========================================================================
 * A resposta guarda o rótulo CONGELADO. Renomear "Cordialidade" para
 * "Atendimento" faz os dois nomes conviverem no relatório — o que é correto, e
 * surpreende quem esperava um rename. Por isso o contador de respostas aparece
 * ao lado de cada critério: ele é o aviso de que ali já existe série histórica.
 */

/** O rascunho de um critério na tela. `id` ausente = ainda não existe no banco. */
interface CriterioNoRascunho {
  readonly id?: string
  rotulo: string
  ativo: boolean
  readonly respostas: number
  /** Chave estável de React — o `id` não serve, porque o novo não tem. */
  readonly chave: string
}

let contadorDeChaves = 0

function novaChave(): string {
  contadorDeChaves += 1
  return `novo-${String(contadorDeChaves)}`
}

function paraRascunho(config: ConfiguracaoDaApi): readonly CriterioNoRascunho[] {
  return config.criterios.map((criterio) => ({
    id: criterio.id,
    rotulo: criterio.rotulo,
    ativo: criterio.ativo,
    respostas: criterio.respostas,
    chave: criterio.id,
  }))
}

export function PesquisaConfigPage(): ReactElement {
  const podeEditar = usePermissao('survey.update')
  const consulta = useConfiguracaoDaPesquisa()
  const salvar = useSalvarConfiguracao()

  const [ativa, setAtiva] = useState(false)
  const [escalaMaxima, setEscalaMaxima] = useState(5)
  const [rotuloMinimo, setRotuloMinimo] = useState('')
  const [rotuloMaximo, setRotuloMaximo] = useState('')
  const [notaDeReview, setNotaDeReview] = useState('')
  const [urlDeReview, setUrlDeReview] = useState('')
  const [mensagem, setMensagem] = useState('')
  const [emailNegativo, setEmailNegativo] = useState('')
  const [criterios, setCriterios] = useState<readonly CriterioNoRascunho[]>([])
  const [carregado, setCarregado] = useState(false)

  const config = consulta.data

  /** Joga a configuração do servidor para dentro do formulário. */
  const aplicar = useCallback((vinda: ConfiguracaoDaApi): void => {
    setAtiva(vinda.ativa)
    setEscalaMaxima(vinda.escalaMaxima)
    setRotuloMinimo(vinda.rotuloDaNotaMinima)
    setRotuloMaximo(vinda.rotuloDaNotaMaxima)
    setNotaDeReview(
      vinda.notaMinimaParaReview === undefined ? '' : String(vinda.notaMinimaParaReview),
    )
    setUrlDeReview(vinda.urlDeReview ?? '')
    setMensagem(vinda.mensagemDeRedirecionamento)
    setEmailNegativo(vinda.emailParaFeedbackNegativo ?? '')
    setCriterios(paraRascunho(vinda))
  }, [])

  // Carrega UMA vez, e não a cada `data`: um refetch de fundo (foco da janela,
  // reconexão) sobrescreveria o formulário no meio da digitação.
  useEffect(() => {
    if (config === undefined || carregado) {
      return
    }
    aplicar(config)
    setCarregado(true)
  }, [config, carregado, aplicar])

  const ativos = useMemo(() => criterios.filter((criterio) => criterio.ativo), [criterios])

  function alterarCriterio(chave: string, mudanca: Partial<CriterioNoRascunho>): void {
    setCriterios((atual) =>
      atual.map((criterio) => (criterio.chave === chave ? { ...criterio, ...mudanca } : criterio)),
    )
  }

  function acrescentar(): void {
    setCriterios((atual) => [
      ...atual,
      { rotulo: '', ativo: true, respostas: 0, chave: novaChave() },
    ])
  }

  /**
   * Remover é DESATIVAR quando o critério já tem resposta.
   *
   * Some da lista só o que nunca foi respondido — esse ainda não existe no
   * relatório de ninguém. O resto vira uma linha desligada, visível, porque a
   * alternativa é o gestor recriar um critério com o mesmo nome e partir a
   * série histórica em duas colunas que ninguém consegue somar depois.
   */
  function remover(chave: string): void {
    setCriterios((atual) => {
      const alvo = atual.find((criterio) => criterio.chave === chave)
      if (alvo === undefined) {
        return atual
      }
      if (alvo.id === undefined) {
        return atual.filter((criterio) => criterio.chave !== chave)
      }
      return atual.map((criterio) =>
        criterio.chave === chave ? { ...criterio, ativo: false } : criterio,
      )
    })
  }

  function confirmar(): void {
    const paraSalvar: readonly CriterioParaSalvar[] = criterios
      .map((criterio, indice) => ({
        ...(criterio.id === undefined ? {} : { id: criterio.id }),
        rotulo: criterio.rotulo.trim(),
        ordem: indice,
        ativo: criterio.ativo,
      }))
      .filter((criterio) => criterio.rotulo !== '')

    if (paraSalvar.filter((criterio) => criterio.ativo).length === 0) {
      toast.error('A pesquisa precisa de ao menos um critério ativo.')
      return
    }

    const nota = notaDeReview.trim() === '' ? undefined : Number(notaDeReview)
    const url = urlDeReview.trim()

    // ⚠️ A GUARDA ESTAVA INVERTIDA, e este comentário fica como registro.
    //
    // Ela bloqueava NOTA-SEM-URL — que a API aceita, e que significa uma coisa
    // legítima: "quero o corte configurado e ninguém redirecionado ainda". E
    // deixava passar URL-SEM-NOTA, que é exatamente o que a API recusa
    // (`aplicarRegrasDaConfiguracao`, no shared), porque uma URL sem limiar
    // redirecionaria TODO MUNDO, inclusive quem deu a nota mínima.
    //
    // O resultado eram os dois erros possíveis ao mesmo tempo: a combinação boa
    // recusada com um texto que não explicava nada, e a ruim indo até a API
    // para voltar num 422 que a tela não sabia atribuir a nenhum campo.
    if (url !== '' && nota === undefined) {
      toast.error(
        'Informe a nota mínima para redirecionar. Sem ela, TODO MUNDO seria convidado a avaliar — inclusive quem deu a nota mais baixa.',
      )
      return
    }

    if (nota !== undefined && (nota < 1 || nota > escalaMaxima)) {
      // Fora da escala o desvio morre em silêncio: `mereceReview` exige que
      // todas as notas alcancem o limiar, e a maior possível é `escalaMaxima`.
      toast.error(`A nota mínima para convidar precisa estar entre 1 e ${String(escalaMaxima)}.`)
      return
    }

    salvar.mutate(
      {
        ativa,
        escalaMinima: 1,
        escalaMaxima,
        rotuloDaNotaMinima: rotuloMinimo,
        rotuloDaNotaMaxima: rotuloMaximo,
        ...(nota === undefined ? {} : { notaMinimaParaReview: nota }),
        urlDeReview: url === '' ? null : url,
        mensagemDeRedirecionamento: mensagem,
        emailParaFeedbackNegativo: emailNegativo.trim() === '' ? null : emailNegativo.trim(),
        criterios: paraSalvar,
      },
      {
        onSuccess: (salva) => {
          // ⚠️ RE-SEMEIA O RASCUNHO COM A RESPOSTA DO SALVAMENTO.
          //
          // O critério que a pessoa acabou de acrescentar foi enviado SEM `id`
          // — é assim que a API sabe que ele é novo. A resposta traz o id que o
          // banco deu. Sem trazê-lo de volta para o estado local, um segundo
          // "Salvar" mandaria o mesmo critério sem id de novo: a API criaria uma
          // SEGUNDA linha e desativaria a primeira, que ficaria fora da lista de
          // enviados. Dois salvamentos seguidos — o gesto mais comum de quem
          // corrige um rótulo logo depois — duplicavam o critério e derrubavam o
          // original.
          //
          // Aqui é seguro sobrescrever, ao contrário do refetch de fundo: este é
          // o instante em que o servidor acabou de aceitar exatamente o que está
          // na tela.
          aplicar(salva)
          toast.success('Configuração da pesquisa salva.')
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  if (consulta.isPending) {
    return <LoadingState rotulo="Carregando a configuração…" linhas={4} />
  }

  if (consulta.isError || config === undefined) {
    return (
      <ErrorState
        titulo="Não foi possível carregar a pesquisa"
        erro={consulta.error}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
      />
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Pesquisa de satisfação</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          O que se pergunta ao cliente quando a locação fecha, e o que acontece com a resposta.
        </p>
      </div>

      {!podeEditar && (
        <Alert>
          <AlertTitle>Somente leitura</AlertTitle>
          <AlertDescription>
            Você pode ver a configuração e o relatório. Alterar os critérios e o limiar de avaliação
            pública exige a permissão de configurar a pesquisa.
          </AlertDescription>
        </Alert>
      )}

      {/* ------------------------------------------------------------------
          Ligar/desligar
          ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>Envio automático</CardTitle>
          <CardDescription>
            Com o envio ligado, toda locação encerrada recebe a pesquisa por e-mail. O link vale 30
            dias e responde uma vez só.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-start gap-3">
          <Checkbox
            id="pesquisa-ativa"
            checked={ativa}
            disabled={!podeEditar}
            onChange={(evento) => {
              setAtiva(evento.target.checked)
            }}
          />
          <Label htmlFor="pesquisa-ativa" className="text-sm font-normal leading-relaxed">
            Enviar a pesquisa ao encerrar a locação
          </Label>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------
          Critérios
          ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>O que se pergunta</CardTitle>
          <CardDescription>
            Até {MAXIMO_DE_CRITERIOS} critérios. Acima disso a taxa de resposta despenca — e é a
            taxa que sustenta o relatório inteiro.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {criterios.map((criterio) => (
            <div
              key={criterio.chave}
              className={`flex items-center gap-2 ${criterio.ativo ? '' : 'opacity-50'}`}
            >
              <GripVertical className="size-4 shrink-0 text-fg-muted" aria-hidden />
              <Input
                value={criterio.rotulo}
                disabled={!podeEditar}
                aria-label="Rótulo do critério"
                placeholder="Ex.: Atendimento"
                onChange={(evento) => {
                  alterarCriterio(criterio.chave, { rotulo: evento.target.value })
                }}
              />
              {criterio.respostas > 0 && (
                <Badge
                  variant="neutral"
                  title="Renomear não reescreve o passado: as respostas antigas guardam o nome que o critério tinha, e os dois aparecem no relatório."
                >
                  {criterio.respostas} resposta{criterio.respostas === 1 ? '' : 's'}
                </Badge>
              )}
              {criterio.ativo ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!podeEditar}
                  aria-label={`Remover ${criterio.rotulo || 'critério'}`}
                  onClick={() => {
                    remover(criterio.chave)
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!podeEditar}
                  aria-label={`Reativar ${criterio.rotulo}`}
                  title="Reativar. Ele volta à pesquisa e ao relatório com a série que já tinha."
                  onClick={() => {
                    alterarCriterio(criterio.chave, { ativo: true })
                  }}
                >
                  <Undo2 className="size-4" />
                </Button>
              )}
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            className="self-start"
            // `ativos`, e não `criterios.length`: o teto da API também é sobre
            // os ATIVOS (ver `pesquisas.dto.ts`), porque a regra é sobre
            // quantas perguntas a PESSOA responde — critério desativado não é
            // perguntado a ninguém.
            disabled={!podeEditar || ativos.length >= MAXIMO_DE_CRITERIOS}
            onClick={acrescentar}
          >
            <Plus className="mr-2 size-4" />
            Acrescentar critério
          </Button>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------
          Escala
          ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>A escala</CardTitle>
          <CardDescription>
            O corte de promotor acompanha a escala escolhida. Hoje, numa escala de 1 a{' '}
            {config.escalaMaxima}, é promotor quem dá {config.minimaDePromotorEfetiva} ou mais.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="escala-maxima">Nota máxima</Label>
            <Select
              id="escala-maxima"
              value={String(escalaMaxima)}
              disabled={!podeEditar}
              onChange={(evento) => {
                setEscalaMaxima(Number(evento.target.value))
              }}
            >
              {[5, 10].map((maxima) => (
                <option key={maxima} value={String(maxima)}>
                  1 a {maxima}
                </option>
              ))}
            </Select>
            <p className="text-xs text-fg-muted">
              Entre {ESCALA_MINIMA_POSSIVEL} e {ESCALA_MAXIMA_POSSIVEL}. Trocar a escala muda o
              corte de promotor, e o relatório recalcula com o corte novo.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="rotulo-minimo">Texto da nota mais baixa</Label>
            <Input
              id="rotulo-minimo"
              value={rotuloMinimo}
              disabled={!podeEditar}
              onChange={(evento) => {
                setRotuloMinimo(evento.target.value)
              }}
            />
          </div>

          <div className="flex flex-col gap-2 sm:col-start-2">
            <Label htmlFor="rotulo-maximo">Texto da nota mais alta</Label>
            <Input
              id="rotulo-maximo"
              value={rotuloMaximo}
              disabled={!podeEditar}
              onChange={(evento) => {
                setRotuloMaximo(evento.target.value)
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------
          A alavanca comercial
          ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliação pública</CardTitle>
          <CardDescription>
            Quem responde bem é convidado a avaliar a empresa publicamente. Quem responde mal cai
            direto para quem cuida disso — e não recebe convite nenhum.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Alert>
            <AlertTitle>⚠️ Todas as notas precisam alcançar o corte</AlertTitle>
            <AlertDescription>
              Não é a média. Quem deu {escalaMaxima} no veículo e 2 no atendimento não é convidado —
              e é justamente essa pessoa que a média esconderia.
            </AlertDescription>
          </Alert>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="nota-review">Nota mínima para convidar</Label>
              <Input
                id="nota-review"
                type="number"
                inputMode="numeric"
                min={1}
                max={escalaMaxima}
                value={notaDeReview}
                disabled={!podeEditar}
                placeholder="Vazio = ninguém é convidado"
                onChange={(evento) => {
                  setNotaDeReview(evento.target.value)
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="url-review">Endereço da página de avaliação</Label>
              <Input
                id="url-review"
                type="url"
                value={urlDeReview}
                disabled={!podeEditar}
                placeholder="https://g.page/r/…"
                onChange={(evento) => {
                  setUrlDeReview(evento.target.value)
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="mensagem-redirecionamento">Mensagem antes de redirecionar</Label>
            <Textarea
              id="mensagem-redirecionamento"
              value={mensagem}
              disabled={!podeEditar}
              rows={2}
              onChange={(evento) => {
                setMensagem(evento.target.value)
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="email-negativo">Para onde vai o alerta de nota baixa</Label>
            <Input
              id="email-negativo"
              type="email"
              value={emailNegativo}
              disabled={!podeEditar}
              placeholder="Vazio = só fica na fila do painel"
              onChange={(evento) => {
                setEmailNegativo(evento.target.value)
              }}
            />
          </div>
        </CardContent>
      </Card>

      {podeEditar && (
        <div className="flex justify-end">
          <Button type="button" disabled={salvar.isPending} onClick={confirmar}>
            {salvar.isPending ? 'Salvando…' : 'Salvar configuração'}
          </Button>
        </div>
      )}

      <MotivosDeCancelamento podeEditar={podeEditar} />
    </div>
  )
}
